import React from "react";
import Dexef from "assets/images/svg/dexef_logo_nav.svg";
import {
  download,
  getInvoicePaymentMethod,
  Invoice,
  InvoiceStatusIcons,
  InvoiceStatusIconsAr,
  isPaid,
  isRefunded,
  isUnpaid
} from "pages/invoices/invoices.service";
import Back from "assets/images/svg/back_blue.svg";
import { Modal } from "react-bootstrap";
import { formatDate, json } from "helpers/data";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { client } from "../../../helpers/client";
import { useLocalize } from "hooks";
import { DiscriminatorVal } from "services/payment-services";

interface InvoicePdfDialogProps {
  invoice: Invoice;
  show: boolean;
  onHide: () => void;
}

function InvoicePdfDialog({ invoice, show, onHide }: InvoicePdfDialogProps) {
  const { t } = useTranslation("invoices");
  const navigate = useNavigate();
  const pdfRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <Modal size="xl" show={show} onHide={onHide} className="modal-pdf">
      <Modal.Body>
        <div className="header">
          <div>
            <img className="back" src={Back} alt="back" onClick={onHide} />
            <h1 className="title">
              {t("invoice") as string} #{invoice.serial}
            </h1>
          </div>
          <div className="action">
            <button className="btn-White" onClick={() => download([parseInt(invoice.id)])}>
              {t("download") as string}
            </button>
            {isUnpaid(invoice) && (
              <button className="btn-blue" onClick={() => navigate(`/payment-details?invoiceId=${invoice.id}&Discriminate=${DiscriminatorVal[invoice.discriminator]}`)}>
                {t("pay") as string}
              </button>
            )}
          </div>
        </div>
        <InvoicePdf invoice={invoice} innerRef={pdfRef} />
      </Modal.Body>
    </Modal>
  );
}

interface InvoicePdfProps {
  innerRef?: any;
  invoice: Invoice;
}

export function InvoicePdf({ innerRef, invoice }: InvoicePdfProps) {
  const { t } = useTranslation("invoices");
  const { currentLanguageCode } = useLocalize();

  const { data: qrCode = "" } = useQuery(["qr-code", invoice], () =>
    client<string>("/Invoice/GenerateInvoiceQrCode", { params: { invoiceId: invoice.id }, method: "POST" }).then(
      (r) => r.response
    )
  );

  return (
    <div className="pdf" ref={innerRef}>
      <div className="pdf-header d-flex justify-content-between">
        <div className="mt-4">
          <img src={Dexef} alt="" />
          <p>{t("company-address") as string}</p>
          <p>{t("company-city") as string}</p>
          <p>{t("company-tax") as string}</p>
        </div>
        <img
          className="invoice-status"
          src={
            currentLanguageCode === "en"
              ? InvoiceStatusIcons[invoice.invoiceStatusId]
              : InvoiceStatusIconsAr[invoice.invoiceStatusId]
          }
          alt="invoice-status"
        />
      </div>
      <div className="content">
        <div className="qr-pdf">
          <div>
            <h1 className="title">
              {t("invoice") as string} #{invoice.serial}
            </h1>
            <p>
              {t("invoice-date") as string} : <span>{formatDate(invoice.createDate)}</span>
            </p>
            <p>
              {t("period") as string}:
              <span>
                {formatDate(invoice.startDate)} - {formatDate(invoice.endDate)}
              </span>
            </p>
            {(isPaid(invoice) || isRefunded(invoice)) && (
              <>
                <p>
                  {t("payment-date") as string}: <span>{formatDate(invoice.paymentDate)}</span>
                </p>
                <p>
                  {t("payment-method") as string}: <span>{getInvoicePaymentMethod(invoice)?.info}</span>
                </p>
              </>
            )}
          </div>
          <div>
            <img className="img-qr" src={`data:image/*;base64,${qrCode}`} alt="qr" />
          </div>
        </div>
        <div className="invoiceTo">
          <h2>{t("invoice-to") as string}</h2>
          <p>
            {t("account") as string}: {invoice.customer?.account}
          </p>
          <p>
            {t("name") as string}: {invoice.customer?.name}{" "}
          </p>
          <p>
            {t("tax-reg") as string}: {invoice.customer?.taxReg}
          </p>
          <p>
            {t("address")as string}: {invoice.customer?.address}
          </p>
        </div>
        <div className="product-table">
          <table>
            <thead>
              <tr>
                <th>{t("product") as string}</th>
                <th>{t("price-level") as string}</th>
                <th>{t("sub-type") as string}</th>
                <th>{t("discount") as string}</th>
                <th>{t("net-price") as string}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span className="bold">{json(invoice.productTitle)}</span>
                  <span>
                    {t("expire-on") as string} {formatDate(invoice.expirationDate)}
                  </span>
                </td>
                <td>
                  {invoice.priceLevel?.numberOfLicenses ?? 0} {t("licenses") as string}
                </td>
                <td>{json(invoice.subscription?.name)}</td>
                <td className="green">
                  {invoice.discount} {json(invoice.currency)}
                </td>
                <td>
                  {invoice.subTotal} {json(invoice.currency)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="total">
          <div className="d-flex justify-content-between">
            <h1>{t("subtotal") as string}</h1>
            <p>
              {invoice.subTotal} {json(invoice.currency)}
            </p>
          </div>
          <div className="d-flex justify-content-between">
            <h2>{t("applicable-vat") as string}</h2>
            <span>{invoice.vatPercentage}%</span>
          </div>
          <div className="d-flex justify-content-between">
            <h2>{t("vat-value") as string}</h2>
            <span>
              {invoice.totalVatAmount} {json(invoice.currency)}
            </span>
          </div>
          <hr className="hr" />
          <div className="d-flex justify-content-between">
            <h1 className="h1-total">{t("total") as string}</h1>
            <p className="p-total">
              {invoice.total} {json(invoice.currency)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoicePdfDialog;

// function downloadPdf() {
//   html2canvas(pdfRef.current!).then((c) => {
//     const doc = new JsPDF({ unit: "px", format: [1106, 1088] });
//     doc.addImage(c, 0, 0, 1106, 1088);
//     doc.save();
//   });
// }
